import { useTranslation } from "react-i18next";
import { TokenCostPanel } from "@/components/tokens/TokenCostPanel";
import { ToolCostSection } from "@/components/tokens/ToolCostSection";
import { TokenEconomyPanel } from "@/components/tokens/TokenEconomyPanel";
import { StoryCostTable } from "@/components/project/StoryCostTable";
import { SkillCostChart } from "@/components/project/SkillCostChart";
import { DeliveryMetricsSection } from "@/components/metrics/DeliveryMetricsSection";
import { useStoryCosts } from "@/hooks/useStoryCosts";

interface MetricsPanelProps {
  projectSlug: string;
}

/**
 * MetricsPanel composes:
 * 0. DeliveryMetricsSection -- real delivery KPIs, project-scoped (S16430.6).
 * 1. TokenCostPanel (reused wholesale, SD-1) -- owns its own period state,
 *    loading/error/empty.
 * 2. ToolCostSection -- per-tool subtotals (S16430.4, SD1).
 * 3. SkillCostChart -- aggregated by_skill from fan-out (D12).
 * 4. StoryCostTable -- per-story cost rows with incremental loading.
 * 5. TokenEconomyPanel -- cost efficiency trend (S16430.4, SD1).
 *
 * Independent sections, no all-or-nothing gate (SD-6).
 */
export function MetricsPanel({ projectSlug }: MetricsPanelProps) {
  const { t } = useTranslation();
  const {
    storyCosts,
    skillCosts,
    settledCount,
    totalRefs,
    loading,
    error: _error,
  } = useStoryCosts(projectSlug);

  return (
    <div className="space-y-8">
      {/* Section 0: Delivery metrics -- real WorkLifecycle-derived KPIs (S16430.6) */}
      <DeliveryMetricsSection projectSlug={projectSlug} title={t("delivery.title")} />

      {/* Section 1: Token cost overview (reused, SD-1) */}
      <TokenCostPanel projectSlug={projectSlug} />

      {/* Section 2: Tool cost subtotals (S16430.4, SD1) */}
      <ToolCostSection projectSlug={projectSlug} />

      {/* Section 3: Cost by skill (D12) */}
      <SkillCostChart skillCosts={skillCosts} settledCount={settledCount} />

      {/* Section 4: Per-story cost table (D10) */}
      <StoryCostTable
        rows={storyCosts}
        totalRefs={totalRefs}
        loading={loading}
      />

      {/* Section 5: Cost efficiency trend (S16430.4, SD1) */}
      <TokenEconomyPanel projectSlug={projectSlug} />
    </div>
  );
}
